import { ProjectActions } from '@octopus-do/octopus-project-core'
import { CrawlImportActions } from '@store/crawlImport'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { managerActions } from '@store/reducers/manager/actions'
import { getId } from '@store/utils'
import { useDispatch, useStore } from 'react-redux'

import { addFilledBlock, addFilledChild } from '@/hooks/ai/actionCreators'
import type { AppState, ProjectOrigin } from '@/types'
import type { ImportPayload } from '../model'
import { buildImportModel, MIN_PAGES_TO_EXPAND } from '../model'

type UseCommitProps = {
  teamId?: string | null
  folderId?: string | null
  projectOrigin?: ProjectOrigin
  popupDownloadSitemapXml: boolean
  fallbackToTrialOnLimit?: boolean
}

/**
 * Lands a crawl in a BRAND-NEW project: wipes whatever is in the store, replays the
 * empty-project boilerplate, adds the crawled pages, then creates the project.
 *
 * Do not reach for this from inside an open project — the reset it starts with clears
 * the whole manager slice, meta included. Landing a crawl in a project that already
 * exists is useCommitIntoProject.
 */
const useCommit = ({
  teamId,
  projectOrigin,
  popupDownloadSitemapXml,
  fallbackToTrialOnLimit = false,
}: UseCommitProps) => {
  const dispatch = useDispatch()
  const store = useStore()

  const handleCommit = async (payload: ImportPayload) => {
    const model = buildImportModel(payload, getId)

    dispatch(managerActions.initEmptyProjectActionsSaga())

    model.nodes.forEach((node) => {
      dispatch(
        // @TODO review this and fix
        // @ts-expect-error - Argument of type 'AppThunk<void>' is not assignable to parameter of type 'UnknownAction'
        addFilledChild({
          parentId: node.parentId,
          id: node.id,
          url: node.url,
          name: node.name,
          description: node.seoDescription,
          h1: node.seoH1,
          slug: node.seoSlug,
          title: node.seoTitle,
          index: node.childIndex,
          // Kept verbatim, cast and all: a root has no parent, so this evaluates to null
          // rather than false, and roots are never collapsed however many children they
          // hold. It only type-checked before because the node was implicitly any. Left
          // alone on purpose — this path has to behave exactly as it shipped.
          collapsed: (node.parentId &&
            node.childrenNumber >= MIN_PAGES_TO_EXPAND) as boolean,
        })
      )
    })

    model.blocks.forEach((block) => {
      const color = block.colorHex
        ? migrationProjectSelector.getProjectColorByHex(block.colorHex)(
            store.getState() as AppState
          )
        : null

      dispatch(
        // @TODO review this and fix
        // @ts-expect-error - Argument of type 'AppThunk<void>' is not assignable to parameter of type 'UnknownAction'
        addFilledBlock({
          id: block.id,
          index: block.childIndex,
          title: block.name,
          nodeId: block.nodeId,
          colorId: color?.id ?? null,
        })
      )
    })

    if (model.siteTitle) {
      dispatch(ProjectActions.settings.updateTitle({ title: model.siteTitle }))
    }

    // The blocks the crawler inferred from the markup (OCT-4990) go in last, from a
    // saga that parses their text in chunks and only then saves the project. A page
    // that has them gets no SEO text blocks — the model already left those out.
    dispatch(
      CrawlImportActions.finish({
        pages: model.crawledPages,
        save: {
          popupDownloadSitemapXml,
          teamId,
          ignoreAuthenticated: true,
          origin: projectOrigin,
          fallbackToTrialOnLimit,
        },
      })
    )
  }

  return handleCommit
}

export default useCommit
